const _jsxFileName = '';
function _nullishCoalesce(lhs, rhsFn) {
  if (lhs != null) {
    return lhs;
  } else {
    return rhsFn();
  }
}
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useEffect, useMemo, useState } from 'react';
import {
  Heart,
  Trash2,
  ShoppingBag,
  X,
  AlertCircle,
  RefreshCw,
  LogIn,
  UserPlus,
  Minus,
  Plus,
  Check,
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const SITE_BASE_URL = import.meta.env.VITE_API_URL.replace('/api', '');

const PLACEHOLDER_IMAGE = 'https://placehold.co/80x80/f5f5f5/999999?text=No+Image';

const Wishlist = ({
  onNavigate,
  onClose,
  onSelectProduct,
  onAddToCart,
  user,
  onWishlistCheckout,
}) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  const [isRendered, setIsRendered] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const openTimer = setTimeout(() => {
      setIsVisible(true);
    }, 20);

    return () => clearTimeout(openTimer);
  }, []);

  useEffect(() => {
    loadWishlist();
  }, [retryCount, _optionalChain([user, 'optionalAccess', (_) => _.id])]);

  const hasUser = useMemo(() => !!getUserId(), [user]);

  const handleClose = () => {
    setIsVisible(false);

    setTimeout(() => {
      setIsRendered(false);
      _optionalChain([onClose, 'optionalCall', (_2) => _2()]);
    }, 300);
  };

  function showToast(message, type = 'success') {
    const toast = document.createElement('div');
    toast.className = [
      'fixed top-24 right-4 px-6 py-3 rounded-sm shadow-lg z-[202] text-white',
      type === 'success' ? 'bg-green-600' : 'bg-red-600',
    ].join(' ');
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 3000);
  }

  function getUserId() {
    if (_optionalChain([user, 'optionalAccess', (_3) => _3.id])) return user.id;

    try {
      const userStr = localStorage.getItem('sparklingUser');
      if (userStr) {
        const parsedUser = JSON.parse(userStr);
        if (_optionalChain([parsedUser, 'optionalAccess', (_4) => _4.id]))
          return Number(parsedUser.id);
      }

      const fallbackUserStr = localStorage.getItem('user');
      if (fallbackUserStr) {
        const parsedUser = JSON.parse(fallbackUserStr);
        if (_optionalChain([parsedUser, 'optionalAccess', (_5) => _5.id]))
          return Number(parsedUser.id);
      }

      const id = localStorage.getItem('userId');
      return id ? parseInt(id, 10) : null;
    } catch (e) {
      return null;
    }
  }

  function getGuestId() {
    let guestId = localStorage.getItem('guest_id');
    if (!guestId) {
      guestId = 'guest_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('guest_id', guestId);
    }
    return guestId;
  }

  function updateWishlistCount(items) {
    localStorage.setItem('wishlistCount', String(items.length));
    window.dispatchEvent(new Event('storage'));
    window.dispatchEvent(new Event('wishlist-updated'));
  }

  function toNumber(value, fallback = 0) {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
    return fallback;
  }

  // ============================================================
  // FIXED: getCleanImageUrl - Preserves base64 and blob URLs
  // ============================================================
  function getCleanImageUrl(imageUrl) {
    if (!imageUrl || typeof imageUrl !== 'string') return '';

    const trimmed = imageUrl.trim();
    if (!trimmed) return '';

    // Base64 images - return as-is (these are valid image data)
    if (trimmed.startsWith('data:image/')) return trimmed;

    // Blob URLs - return as-is
    if (trimmed.startsWith('blob:')) return trimmed;

    // Absolute URLs
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;

    // Protocol-relative URLs
    if (trimmed.startsWith('//')) return `https:${trimmed}`;

    // Relative paths starting with slash
    if (trimmed.startsWith('/')) return `${SITE_BASE_URL}${trimmed}`;

    // Other relative paths
    return `${SITE_BASE_URL}/${trimmed}`;
  }

  // ============================================================
  // FIXED: resolveImageFromItem - Properly handles all image types
  // ============================================================
  function resolveImageFromItem(item) {
    // Try all possible image sources in priority order
    const candidates = [
      item.product_image_url, // From joined products table (most reliable)
      item.db_image_url,
      item.db_image,
      item.image_url,
      item.product_image,
      item.image,
    ];

    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'string') continue;

      const trimmed = candidate.trim();
      if (!trimmed) continue;

      // Base64 images - return as-is (no modification needed)
      if (trimmed.startsWith('data:image')) {
        return trimmed;
      }

      // Blob URLs - return as-is
      if (trimmed.startsWith('blob:')) {
        return trimmed;
      }

      // Absolute URLs
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed;
      }

      // Protocol-relative URLs
      if (trimmed.startsWith('//')) {
        const fullUrl = `https:${trimmed}`;
        return fullUrl;
      }

      // Relative paths starting with slash
      if (trimmed.startsWith('/')) {
        const fullUrl = `${SITE_BASE_URL}${trimmed}`;
        return fullUrl;
      }

      // Other relative paths
      const fullUrl = `${SITE_BASE_URL}/${trimmed}`;
      return fullUrl;
    }

    return PLACEHOLDER_IMAGE;
  }

  function normalizeWishlistItem(item) {
    const productId = toNumber(
      _nullishCoalesce(item.product_id, () => item.id),
      0
    );
    const wishlistId = toNumber(
      _nullishCoalesce(
        _nullishCoalesce(item.wishlist_id, () => item.id),
        () => item.product_id
      ),
      productId
    );
    const resolvedImage = resolveImageFromItem(item);

    const rawStock =
      item.stock !== null && item.stock !== undefined ? item.stock : item.available_stock;

    const stockValue =
      rawStock !== null && rawStock !== undefined ? toNumber(rawStock, 0) : undefined;

    return {
      id: productId,
      wishlist_id: wishlistId || productId,
      name: item.name || item.product_name || item.db_name || 'Product',
      price: toNumber(
        _nullishCoalesce(item.price, () => item.product_price),
        0
      ),
      image: resolvedImage,
      image_url: getCleanImageUrl(item.image_url) || resolvedImage,
      product_image: getCleanImageUrl(item.product_image) || resolvedImage,
      product_image_url: getCleanImageUrl(item.product_image_url) || resolvedImage,
      db_image_url: getCleanImageUrl(item.db_image_url) || resolvedImage,
      db_image: getCleanImageUrl(item.db_image) || resolvedImage,
      category: item.category || item.product_category || '',
      description: item.description || item.product_description || '',
      quantity: Math.max(1, toNumber(item.quantity, 1)),
      selected: item.selected === true || item.selected === 1,
      stock: stockValue,
      slug: item.slug,
      created_at: _nullishCoalesce(item.created_at, () => null),
      updated_at: _nullishCoalesce(item.updated_at, () => null),
    };
  }

  async function loadWishlist() {
    setLoading(true);
    setError(null);

    try {
      const userId = getUserId();
      const guestId = getGuestId();

      const url = userId
        ? `${API_BASE_URL}/wishlist/get?user_id=${userId}&_=${Date.now()}`
        : `${API_BASE_URL}/wishlist/get?guest_id=${guestId}&_=${Date.now()}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const rawItems = Array.isArray(data.data) ? data.data : [];
        const normalizedItems = rawItems.map(normalizeWishlistItem);

        setWishlistItems(normalizedItems);
        updateWishlistCount(normalizedItems);
      } else {
        setError(data.message || data.error || 'Failed to load wishlist');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }

  async function removeItem(productId) {
    setUpdating((prev) => ({ ...prev, [productId]: true }));

    try {
      const userId = getUserId();
      const guestId = getGuestId();

      const response = await fetch(`${API_BASE_URL}/wishlist/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          guest_id: guestId,
          product_id: productId,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Failed to remove item');
      }

      const updatedItems = wishlistItems.filter((item) => item.id !== productId);
      setWishlistItems(updatedItems);
      updateWishlistCount(updatedItems);
      showToast(data.message || 'Removed from wishlist');
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'Failed to remove item. Please try again.',
        'error'
      );
      await loadWishlist();
    } finally {
      setUpdating((prev) => ({ ...prev, [productId]: false }));
    }
  }

  async function updateQuantity(itemId, delta) {
    const currentItem = wishlistItems.find((item) => item.id === itemId);
    if (!currentItem) return;

    const newQuantity = Math.max(1, currentItem.quantity + delta);

    if (typeof currentItem.stock === 'number' && newQuantity > currentItem.stock) {
      showToast(`Only ${currentItem.stock} left in stock`, 'error');
      return;
    }

    setUpdating((prev) => ({ ...prev, [itemId]: true }));

    try {
      const userId = getUserId();
      const guestId = getGuestId();

      const response = await fetch(`${API_BASE_URL}/wishlist/update-quantity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          guest_id: guestId,
          product_id: itemId,
          quantity: newQuantity,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        showToast(
          _optionalChain([data, 'optionalAccess', (_12) => _12.message]) ||
            'Failed to update quantity',
          'error'
        );
        await loadWishlist();
        return;
      }

      const updatedItems = wishlistItems.map((item) =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      );
      setWishlistItems(updatedItems);
      updateWishlistCount(updatedItems);
      showToast('Quantity updated', 'success');
    } catch (err) {
      showToast('Failed to update quantity. Please try again.', 'error');
    } finally {
      setUpdating((prev) => ({ ...prev, [itemId]: false }));
    }
  }

  const toggleSelectItem = (id) => {
    setWishlistItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, selected: !item.selected } : item))
    );
  };

  const toggleSelectAll = () => {
    const allSelected = wishlistItems.length > 0 && wishlistItems.every((item) => item.selected);
    setWishlistItems((prev) => prev.map((item) => ({ ...item, selected: !allSelected })));
  };

  async function bulkRemoveItems(productIds) {
    if (!productIds.length) return true;

    try {
      const userId = getUserId();
      const guestId = getGuestId();

      const response = await fetch(`${API_BASE_URL}/wishlist/remove-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          guest_id: guestId,
          product_ids: productIds,
        }),
      });

      const data = await response.json();

      if (data.success) {
        const updatedItems = wishlistItems.filter((item) => !productIds.includes(item.id));
        setWishlistItems(updatedItems);
        updateWishlistCount(updatedItems);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  const handleCheckout = async () => {
    // Guest must log in before checkout
    if (!user || !user.id) {
      handleClose();
      setTimeout(() => onNavigate('auth'), 300);
      return;
    }

    const selectedItems = wishlistItems.filter((item) => item.selected && item.quantity > 0);

    if (selectedItems.length === 0) {
      showToast('Please select at least one item to checkout', 'error');
      return;
    }

    const checkoutItems = selectedItems.map((item) => ({
      product: {
        id: item.id,
        name: item.name,
        price: item.price,
        image: resolveImageFromItem(item),
        category: normalizeCategory(item.category),
        description: item.description,
        stock: _nullishCoalesce(item.stock, () => 0),
        condition: 'Brand New',
        weight: '',
        karat: '',
        goldType: '',
        isNew: false,
        isBestSeller: false,
      },
      quantity: item.quantity,
    }));

    if (onWishlistCheckout) {
      onWishlistCheckout(checkoutItems);
      handleClose();
      return;
    }

    const productIds = selectedItems.map((item) => item.id);
    const checkoutButton = document.querySelector('.checkout-button');

    if (checkoutButton) {
      checkoutButton.disabled = true;
      checkoutButton.textContent = 'Processing...';
    }

    const removed = await bulkRemoveItems(productIds);

    if (!removed) {
      showToast('Warning: Items may remain in wishlist', 'error');
    }

    const checkoutData = {
      items: checkoutItems,
      source: 'wishlist',
      timestamp: Date.now(),
      product_ids: productIds,
    };

    sessionStorage.setItem('wishlistCheckout', JSON.stringify(checkoutData));

    handleClose();
    setTimeout(() => {
      onNavigate('checkout');
    }, 300);
  };

  async function handleAddItemToCart(item) {
    if (!onAddToCart) {
      showToast('Add to cart is not available right now', 'error');
      return;
    }

    if (typeof item.stock === 'number' && item.quantity > item.stock) {
      showToast(`Only ${item.stock} left in stock`, 'error');
      return;
    }

    setUpdating((prev) => ({ ...prev, [item.id]: true }));

    try {
      const product = toProduct(item);
      const success = await onAddToCart(product, item.quantity);

      if (success) {
        showToast('Added to cart');
      } else {
        showToast('Failed to add to cart', 'error');
      }
    } catch (err) {
      showToast('Failed to add to cart', 'error');
    } finally {
      setUpdating((prev) => ({ ...prev, [item.id]: false }));
    }
  }

  function handleImageError(wishlistId) {
    setImageErrors((prev) => ({ ...prev, [wishlistId]: true }));
  }

  function getProductImage(item) {
    if (imageErrors[item.wishlist_id]) return PLACEHOLDER_IMAGE;
    return resolveImageFromItem(item);
  }

  function handleLogin() {
    handleClose();
    setTimeout(() => onNavigate('auth'), 300);
  }

  function handleRegister() {
    handleClose();
    setTimeout(() => onNavigate('register'), 300);
  }

  function normalizeCategory(category) {
    if (category === 'jewelry' || category === 'bags' || category === 'fragrances') {
      return category;
    }
    return 'jewelry';
  }

  function toProduct(item) {
    return {
      id: item.id,
      name: item.name,
      price: item.price,
      image: resolveImageFromItem(item),
      category: normalizeCategory(item.category),
      description: item.description,
      stock: typeof item.stock === 'number' ? item.stock : 0,
      condition: 'Brand New',
      weight: '',
      karat: '',
      goldType: '',
      isNew: false,
      isBestSeller: false,
    };
  }

  const selectedCount = wishlistItems.filter((item) => item.selected).length;
  const totalAmount = wishlistItems
    .filter((item) => item.selected)
    .reduce((sum, item) => sum + item.price * item.quantity, 0);

  if (!isRendered) return null;

  return React.createElement(
    'div',
    {
      className: 'fixed inset-0 z-[200] bg-white md:bg-transparent',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 651 },
    },
    React.createElement('div', {
      className: `hidden md:block absolute inset-0 bg-stone-900/70 backdrop-blur-md transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`,
      onClick: handleClose,
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 652 },
    }),

    React.createElement(
      'div',
      {
        className: `absolute right-0 top-0 h-full w-full md:w-[450px] bg-white shadow-2xl overflow-hidden flex flex-col z-[201] transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`,
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 659 },
      },

      React.createElement(
        'div',
        {
          className:
            'flex items-center justify-between p-6 border-b border-stone-100 bg-stone-50/50',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 664 },
        },
        React.createElement(
          'div',
          {
            className: 'flex items-center gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 665 },
          },
          React.createElement(Heart, {
            size: 20,
            className: 'text-gold-600',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 666 },
          }),
          React.createElement(
            'h2',
            {
              className: 'font-serif text-xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 667 },
            },
            'Wishlist (',
            wishlistItems.length,
            ')'
          )
        ),
        React.createElement(
          'button',
          {
            onClick: handleClose,
            className: 'text-stone-400 hover:text-stone-900 transition-colors p-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 671 },
          },

          React.createElement(X, {
            size: 20,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 675 },
          })
        )
      ),

      !hasUser &&
        React.createElement(
          'div',
          {
            className: 'px-6 pt-4',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 680 },
          },
          React.createElement(
            'div',
            {
              className: 'rounded-sm border border-amber-200 bg-amber-50 p-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 681 },
            },
            React.createElement(
              'p',
              {
                className: 'text-xs text-amber-800 leading-relaxed',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 682 },
              },
              'You are viewing wishlist items as a guest. Login or register to keep them saved to your account.'
            ),
            React.createElement(
              'div',
              {
                className: 'flex gap-2 mt-3',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 686 },
              },
              React.createElement(
                'button',
                {
                  onClick: handleLogin,
                  className:
                    'text-xs uppercase tracking-widest bg-gold-600 text-white px-3 py-2 rounded-sm hover:bg-gold-700 transition-colors flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 687 },
                },

                React.createElement(LogIn, {
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 691 },
                }),
                'Login'
              ),
              React.createElement(
                'button',
                {
                  onClick: handleRegister,
                  className:
                    'text-xs uppercase tracking-widest border border-stone-300 text-stone-700 px-3 py-2 rounded-sm hover:bg-stone-50 transition-colors flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 694 },
                },

                React.createElement(UserPlus, {
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 698 },
                }),
                'Register'
              )
            )
          )
        ),

      React.createElement(
        'div',
        {
          className: 'flex-1 overflow-y-auto p-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 706 },
        },
        loading
          ? React.createElement(
              'div',
              {
                className: 'flex flex-col items-center justify-center h-40',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 708 },
              },
              React.createElement('div', {
                className:
                  'w-8 h-8 border-2 border-gold-600 border-t-transparent rounded-full animate-spin mb-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 709 },
              }),
              React.createElement(
                'p',
                {
                  className: 'text-sm text-stone-500',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 710 },
                },
                'Loading your wishlist...'
              )
            )
          : error
            ? React.createElement(
                'div',
                {
                  className: 'text-center py-10',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 713 },
                },
                React.createElement(AlertCircle, {
                  className: 'mx-auto text-red-400 mb-4',
                  size: 40,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 714 },
                }),
                React.createElement(
                  'p',
                  {
                    className: 'text-red-500 text-sm mb-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 715 },
                  },
                  error
                ),
                React.createElement(
                  'button',
                  {
                    onClick: () => setRetryCount((prev) => prev + 1),
                    className:
                      'flex items-center gap-2 mx-auto text-xs text-gold-600 border border-gold-600 px-4 py-2 rounded-sm hover:bg-gold-50 transition-colors',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 716 },
                  },

                  React.createElement(RefreshCw, {
                    size: 14,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 720 },
                  }),
                  ' Try Again'
                )
              )
            : wishlistItems.length === 0
              ? React.createElement(
                  'div',
                  {
                    className: 'h-full flex flex-col items-center justify-center text-center',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 724 },
                  },
                  React.createElement(
                    'div',
                    {
                      className:
                        'w-20 h-20 bg-stone-50 rounded-full flex items-center justify-center mb-6 text-stone-300',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 725 },
                    },
                    React.createElement(Heart, {
                      size: 30,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 726 },
                    })
                  ),
                  React.createElement(
                    'p',
                    {
                      className: 'text-stone-500 font-light mb-4',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 728 },
                    },
                    'Your wishlist is empty'
                  ),
                  React.createElement(
                    'button',
                    {
                      onClick: () => {
                        handleClose();
                        setTimeout(() => onNavigate('shop'), 300);
                      },
                      className:
                        'text-xs uppercase tracking-widest text-gold-600 border-b border-gold-600 pb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 729 },
                    },
                    'Browse Collections'
                  )
                )
              : React.createElement(
                  React.Fragment,
                  null,
                  React.createElement(
                    'div',
                    {
                      className:
                        'flex justify-between items-center mb-4 pb-2 border-b border-stone-100',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 741 },
                    },
                    React.createElement(
                      'button',
                      {
                        onClick: toggleSelectAll,
                        className:
                          'text-xs uppercase tracking-widest text-gold-600 hover:text-gold-500 flex items-center gap-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 742 },
                      },

                      React.createElement(
                        'div',
                        {
                          className: `w-4 h-4 border rounded flex items-center justify-center ${
                            wishlistItems.length > 0 && wishlistItems.every((item) => item.selected)
                              ? 'bg-gold-600 border-gold-600'
                              : 'border-stone-300'
                          }`,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 746 },
                        },

                        wishlistItems.length > 0 &&
                          wishlistItems.every((item) => item.selected) &&
                          React.createElement(Check, {
                            size: 12,
                            className: 'text-white',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 754 },
                          })
                      ),
                      'Select All'
                    ),
                    React.createElement(
                      'span',
                      {
                        className: 'text-xs text-stone-500',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 759 },
                      },
                      selectedCount,
                      ' selected'
                    )
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'space-y-6',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 762 },
                    },
                    wishlistItems.map((item) => {
                      const productImage = getProductImage(item);
                      const reachedStockLimit =
                        typeof item.stock === 'number' && item.quantity >= item.stock;

                      return React.createElement(
                        'div',
                        {
                          key: item.wishlist_id,
                          className: `flex gap-4 border-b border-stone-100 pb-6 ${
                            updating[item.id] ? 'opacity-50 pointer-events-none' : ''
                          }`,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 769 },
                        },

                        React.createElement(
                          'div',
                          {
                            className: 'flex-shrink-0 pt-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 775 },
                          },
                          React.createElement(
                            'button',
                            {
                              onClick: () => toggleSelectItem(item.id),
                              className:
                                'w-5 h-5 border rounded flex items-center justify-center hover:border-gold-500 transition-colors',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 776 },
                            },

                            item.selected &&
                              React.createElement(Check, {
                                size: 12,
                                className: 'text-gold-600',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 780 },
                              })
                          )
                        ),

                        React.createElement(
                          'div',
                          {
                            className:
                              'w-20 h-20 bg-stone-100 rounded-sm overflow-hidden flex-shrink-0',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 784 },
                          },
                          React.createElement('img', {
                            src: productImage,
                            alt: item.name,
                            className: 'w-full h-full object-cover',
                            onError: () => handleImageError(item.wishlist_id),
                            loading: 'lazy',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 785 },
                          })
                        ),

                        React.createElement(
                          'div',
                          {
                            className: 'flex-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 794 },
                          },
                          React.createElement(
                            'div',
                            {
                              className: 'flex justify-between items-start mb-1 gap-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 795 },
                            },
                            React.createElement(
                              'h4',
                              {
                                className: 'font-serif text-sm text-stone-900 line-clamp-2',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 796 },
                              },
                              item.name
                            ),

                            React.createElement(
                              'button',
                              {
                                onClick: () => removeItem(item.id),
                                disabled: updating[item.id],
                                className:
                                  'text-stone-400 hover:text-red-500 transition-colors disabled:opacity-30',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 800 },
                              },

                              React.createElement(Trash2, {
                                size: 14,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 805 },
                              })
                            )
                          ),

                          React.createElement(
                            'p',
                            {
                              className: 'text-xs text-stone-500 mb-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 809 },
                            },
                            item.category || ''
                          ),

                          React.createElement(
                            'div',
                            {
                              className: 'flex justify-between items-center mt-2 gap-3',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 813 },
                            },
                            React.createElement(
                              'div',
                              {
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 814 },
                              },
                              React.createElement(
                                'span',
                                {
                                  className: 'text-sm font-medium text-stone-900 block',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 815 },
                                },
                                '₱ ',
                                Number(item.price || 0).toLocaleString()
                              )
                            ),

                            React.createElement(
                              'div',
                              {
                                className: 'flex items-center border border-stone-200 rounded-sm',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 820 },
                              },
                              React.createElement(
                                'button',
                                {
                                  onClick: () => updateQuantity(item.id, -1),
                                  disabled: updating[item.id] || item.quantity <= 1,
                                  className:
                                    'p-1 px-2 hover:bg-stone-50 disabled:opacity-30 transition-colors',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 821 },
                                },

                                React.createElement(Minus, {
                                  size: 10,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 826 },
                                })
                              ),
                              React.createElement(
                                'span',
                                {
                                  className: 'px-3 text-xs font-mono min-w-[30px] text-center',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 828 },
                                },
                                item.quantity
                              ),
                              React.createElement(
                                'button',
                                {
                                  onClick: () => updateQuantity(item.id, 1),
                                  disabled: updating[item.id] || reachedStockLimit,
                                  className:
                                    'p-1 px-2 hover:bg-stone-50 disabled:opacity-30 transition-colors',
                                  title: reachedStockLimit
                                    ? 'Stock limit reached'
                                    : 'Increase quantity',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 831 },
                                },

                                React.createElement(Plus, {
                                  size: 10,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 837 },
                                })
                              )
                            )
                          ),

                          typeof item.stock === 'number' &&
                            item.stock <= 5 &&
                            item.stock > 0 &&
                            React.createElement(
                              'p',
                              {
                                className: 'text-[10px] text-amber-600 mt-2',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 843 },
                              },
                              'Only ',
                              item.stock,
                              ' left in stock'
                            ),

                          typeof item.stock === 'number' &&
                            item.stock <= 0 &&
                            React.createElement(
                              'p',
                              {
                                className: 'text-[10px] text-red-600 mt-2',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 849 },
                              },
                              'Out of stock'
                            ),

                          onAddToCart &&
                            React.createElement(
                              'button',
                              {
                                onClick: () => handleAddItemToCart(item),
                                disabled:
                                  updating[item.id] ||
                                  (typeof item.stock === 'number' && item.stock <= 0),
                                className:
                                  'mt-3 text-[10px] uppercase tracking-[0.2em] border border-stone-300 text-stone-700 px-3 py-2 rounded-sm hover:bg-stone-900 hover:text-white transition-colors disabled:opacity-40',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 855 },
                              },
                              'Add to Cart'
                            )
                        )
                      );
                    })
                  )
                )
      ),

      wishlistItems.length > 0 &&
        React.createElement(
          'div',
          {
            className: 'p-6 bg-stone-50 border-t border-stone-200 space-y-4',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 873 },
          },
          selectedCount > 0 &&
            React.createElement(
              'div',
              {
                className: 'flex justify-between items-center pb-2 border-b border-stone-200',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 875 },
              },
              React.createElement(
                'span',
                {
                  className: 'text-xs uppercase font-bold text-stone-500',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 876 },
                },
                'Total (',
                selectedCount,
                ' items)'
              ),
              React.createElement(
                'span',
                {
                  className: 'font-serif text-xl text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 879 },
                },
                '₱ ',
                totalAmount.toLocaleString()
              )
            ),

          React.createElement(
            'button',
            {
              onClick: handleCheckout,
              disabled: selectedCount === 0,
              className:
                'checkout-button w-full bg-stone-900 text-white py-4 uppercase text-xs tracking-[0.2em] hover:bg-gold-600 transition-colors shadow-lg flex items-center justify-center gap-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 885 },
            },

            React.createElement(ShoppingBag, {
              size: 14,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 890 },
            }),
            'Checkout (',
            selectedCount,
            ')'
          ),

          React.createElement(
            'button',
            {
              onClick: () => {
                handleClose();
                setTimeout(() => onNavigate('shop'), 300);
              },
              className:
                'w-full border border-stone-900 text-stone-900 py-3 uppercase text-xs tracking-[0.2em] hover:bg-stone-900 hover:text-white transition-colors rounded-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 894 },
            },
            'Continue Shopping'
          )
        )
    )
  );
};

export default Wishlist;
